/**
 * ForgotPassword — public "request a reset link" page (issue #569 Phase 3;
 * the mailbox-answer UX added by issue #589).
 *
 * Flow:
 *   1. User submits their email here → `POST /api/auth/forgot-password`
 *      (public, rate-limited, enumeration-safe).
 *   2. If the address matches an account, the backend relays a reset email
 *      via the control-plane Email Broker linking to
 *      `${FRONTEND_URL}/reset-password?token=...`. If it does NOT, the
 *      backend relays a "no account for this address" notice to that
 *      mailbox through the same broker — the definitive answer travels to
 *      whoever controls the address, never into the HTTP response.
 *   3. User follows that link to ResetPassword and sets a new password —
 *      or, if nothing arrives, uses "Try a different address" to come back
 *      to the form with the field pre-filled and selected.
 *
 * Enumeration safety: the endpoint's response does not reveal whether the
 * address exists, and neither does this page — EVERY outcome (200, 4xx, or
 * a dead network) renders the same confirmation, which now echoes the
 * SUBMITTED address from local state (never from the response). Reached
 * from the "Forgot password?" link on Login.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API as API_BASE } from '../../lib/api';
import axios from 'axios';
import { Sparkles, CheckCircle, Loader2 } from 'lucide-react';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // The submitted address — truthy while the confirmation shows, whatever
    // the HTTP outcome (issue #589: the confirmation echoes the address from
    // LOCAL state, never from the response, so a 4xx renders exactly what a
    // 200 renders).
    const [sentEmail, setSentEmail] = useState(null);
    const inputRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            await axios.post(`${API_BASE}/auth/forgot-password`, { email });
        } catch {
            // Enumeration-safe by design: a 4xx or network failure must look
            // identical to success. Fall through to the same confirmation —
            // never surface err.response.data.detail here.
        } finally {
            setSubmitting(false);
            setSentEmail(email);
        }
    };

    // "Try a different address" cleared the confirmation and re-rendered the
    // form: focus the input and select its whole value — the user came back
    // precisely to change the address. (Also runs harmlessly on first mount,
    // where the input is empty.)
    useEffect(() => {
        if (!sentEmail && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [sentEmail]);

    if (sentEmail) {
        return (
            <PageShell>
                <div data-testid="forgot-confirmation">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Check your inbox
                    </h2>
                    <p className="text-stone-700 text-sm mb-3">
                        We sent a password reset link to{' '}
                        <strong data-testid="forgot-echoed-email" className="text-stone-900">
                            {sentEmail}
                        </strong>
                        .
                    </p>
                    <p className="text-stone-500 text-sm mb-6">
                        No email after a few minutes? Check your spam folder — or
                        the address may not be the one you registered with.
                    </p>
                    <button
                        type="button"
                        data-testid="forgot-try-different-btn"
                        onClick={() => setSentEmail(null)}
                        className="w-full border border-stone-300 text-stone-700 px-6 py-2.5 rounded-lg font-medium hover:bg-stone-100 transition-colors mb-3"
                    >
                        Try a different address
                    </button>
                    <Link to="/login" className="inline-block bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors">
                        Back to Login
                    </Link>
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <h2 className="text-xl font-semibold text-stone-900 mb-2 text-center" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                Forgot your password?
            </h2>
            <p className="text-stone-500 text-sm mb-6 text-center">
                Enter the email you use to sign in and we'll send you a reset link.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                    <label htmlFor="forgot-email" className="block text-xs font-medium text-stone-700 mb-1">Email address</label>
                    <input
                        id="forgot-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        data-testid="forgot-email-input"
                        className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
                        autoFocus
                        required
                        ref={inputRef}
                    />
                </div>
                <button
                    type="submit"
                    disabled={submitting || !email}
                    data-testid="forgot-submit-btn"
                    className="w-full bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {submitting ? 'Sending…' : 'Send reset link'}
                </button>
            </form>

            <p className="text-center text-xs text-stone-400 mt-6">
                Remembered it?{' '}
                <Link to="/login" className="text-stone-500 hover:text-stone-700 hover:underline">
                    Back to login
                </Link>
            </p>
        </PageShell>
    );
}

function PageShell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                </div>
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 text-center">
                    {children}
                </div>
            </div>
        </div>
    );
}
